/**
 * Sysadmin MCP catalog — server detail (Wave 4 PR-F-rebuild).
 *
 * 5 tabs:
 *   - Overview     — basic metadata + edit form (display name, description,
 *                    icon, marketplace bits, lifecycle controls)
 *   - Auth         — auth scheme JSON editor
 *   - Manifest     — tool manifest JSON editor
 *   - Assignments  — list installed tenants + assign new + revoke existing
 *   - Danger       — version bump, retire, delete
 *
 * Mirrors the Argus models detail page pattern (`models/$id.tsx`)
 * adapted for MCP shape. All mutations use the hooks from
 * `use-sysadmin-mcp.ts` which invalidate appropriate queries on success.
 */

import { useState, useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  CircleDot,
  Network,
  Save,
  Loader2,
  Trash2,
  Eye,
  EyeOff,
  RefreshCcw,
  Archive,
  AlertCircle,
  Plus,
  Server,
  Cloud,
  Globe,
  Shield,
  ShieldCheck,
  ShieldQuestion,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  useSysadminMcpCatalogRow,
  useSysadminMcpInstalls,
  useUpdateMcpCatalog,
  useDeleteMcpCatalog,
  useMcpCatalogLifecycle,
  useAssignMcpToTenant,
  useRevokeMcpAssignment,
  MCP_HOSTING_MODES,
  MCP_PROTOCOLS,
  MCP_TRUST_LEVELS,
  MCP_MARKETPLACE_CATEGORIES,
  type SysadminMcpCatalogRow,
  type InstallReportRow,
  type McpHostingMode,
  type McpTrustLevel,
} from '@/lib/use-sysadmin-mcp';
import { useTenants } from '@/lib/use-tenants';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/catalog/mcp/$id')({
  component: McpServerDetailPage,
});

function McpServerDetailPage() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = useSysadminMcpCatalogRow(id);

  // Issue #982 / #1119 — surface an honest "Not found / 404" state when
  // the detail row is missing. Four failure modes converge on the same
  // user-facing copy:
  //   1. Backend returns 404 NOT_FOUND for a real-but-deleted id
  //   2. Backend returns 400 for non-UUID-shaped id (legacy path)
  //   3. Backend returns 422 VALIDATION_ERROR for non-UUID-shaped id
  //      — this is the current shape produced by the global Zod error
  //      handler in plugins/error-handler.ts. Pre-fix the UI only
  //      treated 404+400 as not-found so a non-UUID URL like
  //      `qa-nonsense-id-cycle-3-6` rendered the generic error card
  //      ("Error loading MCP server: Validation failed") that didn't
  //      contain the "not found" copy the SYS-CATALOG-024 evidence
  //      regex checks for. Codex Test 5 reproduced this on 2026-05-22
  //      and filed #1119; we now also treat 422 as not-found so the
  //      operator gets a consistent honest empty state regardless of
  //      whether the id is real-but-missing or syntactically invalid.
  //   4. Backend returns 403 for a tenant token reaching the sysadmin
  //      catalog — the route gate already covers this, but if it ever
  //      slips through the operator still needs a clear cue
  const errStatus = (error as unknown as { status?: number } | undefined)?.status;
  const isNotFound =
    errStatus === 404 || errStatus === 400 || errStatus === 422;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/sysadmin/catalog/mcp"
          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to catalog
        </Link>
      </div>

      {error && isNotFound ? (
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto" />
            <div className="text-base font-semibold">MCP server not found</div>
            <div className="text-xs text-muted-foreground">
              No such MCP server (404). The id <code className="font-mono">{id}</code>{' '}
              is either invalid or no longer in the catalog.
            </div>
            <Link
              to="/sysadmin/catalog/mcp"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'mt-2')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to MCP catalog
            </Link>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-destructive text-sm">
            <AlertCircle className="h-5 w-5 mx-auto mb-2" />
            Error loading MCP server: {error.message}
          </CardContent>
        </Card>
      ) : isLoading || !data ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm animate-pulse">
            Loading MCP server detail…
          </CardContent>
        </Card>
      ) : (
        <DetailBody row={data} />
      )}
    </div>
  );
}

// ─── Detail body (data is loaded) ─────────────────────────────────────

function DetailBody({ row }: { row: SysadminMcpCatalogRow }) {
  return (
    <>
      <HeaderCard row={row} />
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="auth">Auth</TabsTrigger>
          <TabsTrigger value="manifest">Manifest</TabsTrigger>
          <TabsTrigger value="assignments">
            Assignments ({row.activeInstallCount})
          </TabsTrigger>
          <TabsTrigger value="danger">Danger</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab row={row} />
        </TabsContent>
        <TabsContent value="auth">
          <AuthTab row={row} />
        </TabsContent>
        <TabsContent value="manifest">
          <ManifestTab row={row} />
        </TabsContent>
        <TabsContent value="assignments">
          <AssignmentsTab row={row} />
        </TabsContent>
        <TabsContent value="danger">
          <DangerTab row={row} />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ─── Header card ──────────────────────────────────────────────────────

function HeaderCard({ row }: { row: SysadminMcpCatalogRow }) {
  const lifecycle = useMcpCatalogLifecycle();
  const HostingIcon = row.hostingMode === 'platform_hosted'
    ? Server
    : row.hostingMode === 'remote_hosted'
      ? Cloud
      : Globe;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Network className="h-5 w-5" />
              {row.displayName}
              <LifecycleBadge row={row} />
              <TrustBadge level={row.trustLevel} />
            </CardTitle>
            <div className="text-sm text-muted-foreground font-mono mt-0.5">
              {row.slug}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              {row.isPublished ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      title="Unpublish — hide from catalog (existing installs unaffected)"
                      onClick={() => lifecycle.unpublish.mutate({ id: row.id })}
                      disabled={lifecycle.unpublish.isPending}
                    >
                      {lifecycle.unpublish.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <EyeOff className="h-4 w-4 mr-1" />
                      )}
                      Unpublish
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Hide from tenant marketplace. Existing installs unaffected.
                  </TooltipContent>
                </Tooltip>
              ) : !row.retiredAt ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      title="Publish — make visible to tenants"
                      onClick={() => lifecycle.publish.mutate({ id: row.id })}
                      disabled={lifecycle.publish.isPending}
                    >
                      {lifecycle.publish.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Eye className="h-4 w-4 mr-1" />
                      )}
                      Publish
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Visible to tenants in marketplace.</TooltipContent>
                </Tooltip>
              ) : null}
            </TooltipProvider>
          </div>
        </div>
        <CardDescription className="mt-2">{row.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Stat
            label="Hosting"
            value={
              <span className="inline-flex items-center gap-1.5">
                <HostingIcon className="h-3.5 w-3.5" />
                {hostingLabel(row.hostingMode)}
              </span>
            }
          />
          <Stat label="Protocol" value={<span className="font-mono">{row.protocol}</span>} />
          <Stat label="Version" value={`v${row.version}`} />
          <Stat label="Active installs" value={row.activeInstallCount} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────

function OverviewTab({ row }: { row: SysadminMcpCatalogRow }) {
  const update = useUpdateMcpCatalog();
  const [form, setForm] = useState({
    displayName: row.displayName,
    description: row.description,
    icon: row.icon,
    hostingMode: row.hostingMode,
    protocol: row.protocol,
    marketplaceCategory: row.marketplaceCategory,
    trustLevel: row.trustLevel,
    author: row.author,
    license: row.license,
    dockerImage: row.dockerImage ?? '',
    installInstructions: row.installInstructions ?? '',
    requiresAuth: row.requiresAuth,
  });
  const [saved, setSaved] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaved('saving');
    setError(null);
    try {
      await update.mutateAsync({
        id: row.id,
        displayName: form.displayName,
        description: form.description,
        icon: form.icon,
        hostingMode: form.hostingMode,
        protocol: form.protocol,
        marketplaceCategory: form.marketplaceCategory,
        trustLevel: form.trustLevel,
        author: form.author,
        license: form.license,
        dockerImage: form.dockerImage || null,
        installInstructions: form.installInstructions || null,
        requiresAuth: form.requiresAuth,
      });
      setSaved('success');
    } catch (err) {
      setSaved('error');
      setError(err instanceof Error ? err.message : 'Save failed.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Server metadata</CardTitle>
        <CardDescription>
          Slug is immutable. All other fields editable.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid gap-2 md:col-span-2">
            <Label>Display name</Label>
            <Input
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label>Icon (lucide name)</Label>
            <Input
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label>Docker image</Label>
            <Input
              value={form.dockerImage}
              onChange={(e) => setForm({ ...form, dockerImage: e.target.value })}
              placeholder="optional"
            />
          </div>
          <div className="grid gap-2">
            <Label>Hosting mode</Label>
            <Select
              value={form.hostingMode}
              onValueChange={(v) => setForm({ ...form, hostingMode: v as never })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MCP_HOSTING_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {hostingLabel(m as McpHostingMode)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Protocol</Label>
            <Select
              value={form.protocol}
              onValueChange={(v) => setForm({ ...form, protocol: v as never })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MCP_PROTOCOLS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Category</Label>
            <Select
              value={form.marketplaceCategory}
              onValueChange={(v) =>
                setForm({ ...form, marketplaceCategory: v as never })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MCP_MARKETPLACE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Trust level</Label>
            <Select
              value={form.trustLevel}
              onValueChange={(v) => setForm({ ...form, trustLevel: v as never })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MCP_TRUST_LEVELS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Author</Label>
            <Input
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label>License</Label>
            <Input
              value={form.license}
              onChange={(e) => setForm({ ...form, license: e.target.value })}
            />
          </div>
          <div className="md:col-span-2 grid gap-2">
            <Label>Install instructions</Label>
            <Textarea
              rows={4}
              value={form.installInstructions}
              onChange={(e) =>
                setForm({ ...form, installInstructions: e.target.value })
              }
              placeholder="optional, markdown-friendly"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="requiresAuth"
              checked={form.requiresAuth}
              onChange={(e) =>
                setForm({ ...form, requiresAuth: e.target.checked })
              }
              className="rounded border-input"
            />
            <Label htmlFor="requiresAuth" className="cursor-pointer">
              Requires authentication
            </Label>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-4">
          <span className="text-xs text-muted-foreground">
            {saved === 'success' && 'Saved.'}
            {saved === 'saving' && 'Saving…'}
          </span>
          <Button
            onClick={handleSave}
            disabled={update.isPending}
            title="Save metadata changes"
          >
            {update.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Auth tab ─────────────────────────────────────────────────────────

function AuthTab({ row }: { row: SysadminMcpCatalogRow }) {
  const update = useUpdateMcpCatalog();
  const [text, setText] = useState(JSON.stringify(row.authScheme, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setParseError(null);
    setSaveError(null);
    setSaved(false);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setParseError('auth_scheme must be a JSON object.');
        return;
      }
    } catch (err) {
      setParseError(
        `JSON parse error: ${err instanceof Error ? err.message : String(err)}`,
      );
      return;
    }
    try {
      await update.mutateAsync({ id: row.id, authScheme: parsed });
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Auth scheme</CardTitle>
        <CardDescription>
          JSON object — typically{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">
            {`{ "scheme": "oauth2", "provider": "github" }`}
          </code>
          {' '}or{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">
            {`{ "scheme": "none" }`}
          </code>
          . Tenants only see <code className="text-xs">scheme</code> in the
          redacted view (per I-M6.7).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Textarea
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="font-mono text-xs"
          spellCheck={false}
        />
        {parseError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{parseError}</span>
          </div>
        )}
        {saveError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{saveError}</span>
          </div>
        )}
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-xs text-muted-foreground">
            {saved && 'Saved.'}
          </span>
          <Button onClick={handleSave} disabled={update.isPending} title="Save auth scheme">
            {update.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save auth scheme
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Manifest tab ─────────────────────────────────────────────────────

function ManifestTab({ row }: { row: SysadminMcpCatalogRow }) {
  const update = useUpdateMcpCatalog();
  const [text, setText] = useState(JSON.stringify(row.manifest, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setParseError(null);
    setSaveError(null);
    setSaved(false);
    let parsed: unknown[];
    try {
      const v = JSON.parse(text);
      if (!Array.isArray(v)) {
        setParseError('manifest must be a JSON array.');
        return;
      }
      parsed = v;
    } catch (err) {
      setParseError(
        `JSON parse error: ${err instanceof Error ? err.message : String(err)}`,
      );
      return;
    }
    try {
      await update.mutateAsync({ id: row.id, manifest: parsed });
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Tool manifest</CardTitle>
        <CardDescription>
          JSON array of tool definitions exposed by this MCP server. Tenants
          see only the <strong>count</strong> in the redacted view.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Textarea
          rows={16}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="font-mono text-xs"
          spellCheck={false}
        />
        {parseError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{parseError}</span>
          </div>
        )}
        {saveError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{saveError}</span>
          </div>
        )}
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-xs text-muted-foreground">
            {saved && 'Saved.'}
          </span>
          <Button onClick={handleSave} disabled={update.isPending} title="Save tool manifest">
            {update.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save manifest
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Assignments tab ──────────────────────────────────────────────────

function AssignmentsTab({ row }: { row: SysadminMcpCatalogRow }) {
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const installs = useSysadminMcpInstalls(row.id, includeRevoked);
  const tenants = useTenants();
  const assign = useAssignMcpToTenant();
  const revoke = useRevokeMcpAssignment();

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [revokeFor, setRevokeFor] = useState<InstallReportRow | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const assignedOrgIds = useMemo(
    () =>
      new Set(
        (installs.data?.items ?? [])
          .filter((i) => !i.revokedAt)
          .map((i) => i.organizationId),
      ),
    [installs.data],
  );

  const availableTenants = useMemo(
    () =>
      (tenants.data?.tenants ?? []).filter(
        (t) => !assignedOrgIds.has(t.id) && t.status === 'active',
      ),
    [tenants.data, assignedOrgIds],
  );

  const handleAssign = async () => {
    if (!selectedTenantId) return;
    setAssignError(null);
    try {
      await assign.mutateAsync({
        organizationId: selectedTenantId,
        systemServerId: row.id,
      });
      setAssignDialogOpen(false);
      setSelectedTenantId(null);
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Assign failed.');
    }
  };

  const handleRevoke = async () => {
    if (!revokeFor) return;
    try {
      await revoke.mutateAsync({
        id: revokeFor.id,
        reason: revokeReason.trim() || undefined,
      });
      setRevokeFor(null);
      setRevokeReason('');
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Revoke failed.');
    }
  };

  const items = installs.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Tenant assignments</CardTitle>
            <CardDescription>
              Tenants with this MCP server installed. Revoke disables access
              while preserving audit history.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
                className="rounded border-input"
              />
              Show revoked
            </label>
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" title="Assign this MCP server to a tenant">
                  <Plus className="h-4 w-4 mr-1" />
                  Assign to tenant
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assign MCP server</DialogTitle>
                  <DialogDescription>
                    Pick the tenant that should get access to{' '}
                    <strong>{row.displayName}</strong>.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <Label>Tenant</Label>
                  {tenants.isLoading ? (
                    <div className="text-sm text-muted-foreground animate-pulse">
                      Loading tenants…
                    </div>
                  ) : availableTenants.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      All active tenants already have this assigned.
                    </div>
                  ) : (
                    <Select
                      value={selectedTenantId ?? ''}
                      onValueChange={(v) => setSelectedTenantId(v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a tenant…" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTenants.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} <span className="font-mono text-xs text-muted-foreground">({t.slug})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {assignError && (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{assignError}</span>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setAssignDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssign}
                    disabled={!selectedTenantId || assign.isPending}
                  >
                    {assign.isPending && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Assign
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {installs.error ? (
          <div className="p-8 text-center text-destructive text-sm">
            Failed to load assignments: {installs.error.message}
          </div>
        ) : installs.isLoading ? (
          <div className="p-8 text-center text-muted-foreground text-sm animate-pulse">
            Loading assignments…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No tenants have this MCP server installed yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead className="text-right">Version</TableHead>
                <TableHead>Health</TableHead>
                <TableHead>Installed</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((install) => (
                <TableRow key={install.id}>
                  <TableCell>
                    <div className="font-medium">{install.organizationName}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {install.organizationId.slice(0, 8)}…
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    v{install.installedVersion}
                  </TableCell>
                  <TableCell>
                    <HealthBadge status={install.healthStatus} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(install.installedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {install.revokedAt ? (
                      <Badge
                        variant="outline"
                        className="text-rose-700 border-rose-300 text-[10px]"
                        title={
                          install.revocationReason
                            ? `Reason: ${install.revocationReason}`
                            : undefined
                        }
                      >
                        revoked
                      </Badge>
                    ) : install.isActive ? (
                      <Badge
                        variant="outline"
                        className="text-emerald-700 border-emerald-300 gap-1 text-[10px]"
                      >
                        <CircleDot className="h-3 w-3" />
                        active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-[10px]">
                        inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {!install.revokedAt && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeFor(install)}
                        title="Revoke this tenant's access"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Revoke confirm dialog */}
      <AlertDialog
        open={!!revokeFor}
        onOpenChange={(o) => !o && setRevokeFor(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke MCP assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the MCP server from{' '}
              <strong>{revokeFor?.organizationName}</strong>'s available tools
              immediately. Audit history is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2 py-2">
            <Label>Reason (optional)</Label>
            <Input
              placeholder="e.g. tenant offboarding"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoke.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ─── Danger tab ───────────────────────────────────────────────────────

function DangerTab({ row }: { row: SysadminMcpCatalogRow }) {
  const lifecycle = useMcpCatalogLifecycle();
  const del = useDeleteMcpCatalog();
  const navigate = useNavigate();

  const handleDelete = async () => {
    await del.mutateAsync({ id: row.id });
    navigate({ to: '/sysadmin/catalog/mcp' });
  };

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-base text-destructive">Danger zone</CardTitle>
        <CardDescription>
          Operations that affect tenants. Confirmation required.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Version bump */}
        <div className="flex items-start justify-between gap-4 border rounded-md p-3">
          <div>
            <div className="font-medium text-sm">Bump version</div>
            <p className="text-xs text-muted-foreground mt-1">
              Publishes v{row.version + 1}. Existing installs stay pinned at
              v{row.version}; new installs pick up the new version.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                title={`Bump to v${row.version + 1}`}
              >
                <RefreshCcw className="h-4 w-4 mr-1" />
                Bump
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Bump to v{row.version + 1}?</AlertDialogTitle>
                <AlertDialogDescription>
                  All <strong>{row.activeInstallCount} existing installs</strong>{' '}
                  remain pinned at v{row.version} until they explicitly upgrade.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => lifecycle.versionBump.mutate({ id: row.id })}
                >
                  {lifecycle.versionBump.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Bump version
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Retire */}
        {!row.retiredAt && (
          <div className="flex items-start justify-between gap-4 border rounded-md p-3">
            <div>
              <div className="font-medium text-sm">Retire</div>
              <p className="text-xs text-muted-foreground mt-1">
                Hides from catalog and prevents new installs. Existing installs
                continue to work until tenants revoke them.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" title="Retire this server">
                  <Archive className="h-4 w-4 mr-1" />
                  Retire
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Retire {row.displayName}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {row.activeInstallCount > 0 ? (
                      <>
                        <strong>{row.activeInstallCount} tenants</strong> currently
                        have this installed. They will continue working but no new
                        installs are allowed.
                      </>
                    ) : (
                      'This server will be hidden from the catalog and unpublished.'
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => lifecycle.retire.mutate({ id: row.id })}
                  >
                    {lifecycle.retire.isPending && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Retire
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* Delete (soft) */}
        <div className="flex items-start justify-between gap-4 border rounded-md p-3 border-destructive/40">
          <div>
            <div className="font-medium text-sm text-destructive">Soft delete</div>
            <p className="text-xs text-muted-foreground mt-1">
              Equivalent to retire — the row is preserved in the database for
              audit. Hard delete is intentionally not exposed.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                title="Soft delete (sets retiredAt)"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Soft delete {row.displayName}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Sets retiredAt and unpublishes. The row remains in the database
                  for audit.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {del.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Soft delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function hostingLabel(mode: McpHostingMode): string {
  switch (mode) {
    case 'platform_hosted': return 'Platform-hosted';
    case 'remote_hosted': return 'Remote-hosted';
    case 'rest_wrapped': return 'REST-wrapped';
  }
}

function LifecycleBadge({ row }: { row: SysadminMcpCatalogRow }) {
  if (row.retiredAt) {
    return (
      <Badge variant="outline" className="text-muted-foreground text-[10px]">
        retired
      </Badge>
    );
  }
  if (!row.isPublished) {
    return (
      <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">
        draft
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-emerald-700 border-emerald-300 gap-1 text-[10px]"
    >
      <CircleDot className="h-3 w-3" />
      published
    </Badge>
  );
}

function TrustBadge({ level }: { level: McpTrustLevel }) {
  const Icon =
    level === 'official'
      ? ShieldCheck
      : level === 'verified'
        ? Shield
        : level === 'community'
          ? ShieldQuestion
          : AlertTriangle;
  const tone =
    level === 'official'
      ? 'border-emerald-300 text-emerald-700'
      : level === 'verified'
        ? 'border-blue-300 text-blue-700'
        : level === 'community'
          ? 'border-amber-300 text-amber-700'
          : 'border-rose-300 text-rose-700';
  return (
    <Badge variant="outline" className={cn('text-[10px] gap-1', tone)}>
      <Icon className="h-3 w-3" />
      {level}
    </Badge>
  );
}

function HealthBadge({ status }: { status: string }) {
  const tone =
    status === 'healthy'
      ? 'border-emerald-300 text-emerald-700'
      : status === 'degraded'
        ? 'border-amber-300 text-amber-700'
        : status === 'down'
          ? 'border-rose-300 text-rose-700'
          : 'border-muted text-muted-foreground';
  return (
    <Badge variant="outline" className={cn('text-[10px]', tone)}>
      {status}
    </Badge>
  );
}
